import {
  catchError,
  from,
  map,
  ObservableInput,
  OperatorFunction,
  switchMap,
  throwError,
} from 'rxjs';

export const logError = <T>() =>
  catchError<T, ObservableInput<never>>((error: any) => {
    console.error(error);
    return throwError(() => error);
  });

/**
 * An RxJS operator that performs a side-effecting observable for each value in the source,
 * but suppresses the side effect's result and re-emits the original value.
 *
 * This is useful when you want to trigger an observable (e.g. for saving data or logging)
 * and continue the stream with the original value, replacing any previous inner subscription.
 *
 * Equivalent to:
 *   switchMap(value => project().pipe(map(() => value)))
 *
 * @param project A function that returns an Observable to perform as a side effect.
 * @returns An OperatorFunction that maps each source value to the result of the side effect,
 *          and re-emits the original source value.
 */
export const squeezeMap =
  <T, O extends ObservableInput<unknown>>(project: (value: T) => O): OperatorFunction<T, T> =>
  (observable) =>
    observable.pipe(
      switchMap((previousValue) => from(project(previousValue)).pipe(map(() => previousValue))),
    );

/**
 * A shortcut for catchError(() => throwError(() => "error message")).
 */
export const onErrorThrow =
  <T, O extends ObservableInput<O>>(error: string): OperatorFunction<T, T> =>
  (observable) =>
    observable.pipe(catchError(() => throwError(() => error)));
